import React from 'react'
import { motion } from 'framer-motion'
import { ArrowLeft, Shield } from 'lucide-react'
import { Link } from 'react-router-dom'

export default function Privacy() {
  return (
    <div className="page container" style={{ maxWidth: 800 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Link 
          to="/" 
          className="btn btn-ghost" 
          style={{ marginBottom: 'var(--space-lg)' }}
        >
          <ArrowLeft size={18} />
          Back
        </Link>

        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 'var(--space-md)',
          marginBottom: 'var(--space-2xl)'
        }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: 'var(--radius-md)',
            background: 'var(--accent-blue-muted)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Shield size={24} style={{ color: 'var(--accent-blue)' }} />
          </div>
          <div>
            <h1>Privacy Policy</h1>
            <p className="text-secondary">Last updated: January 2025</p>
          </div>
        </div>

        <div className="glass-card-static" style={{ padding: 'var(--space-2xl)' }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 'var(--space-xl)',
            lineHeight: 1.8
          }}>
            <section>
              <h3 style={{ marginBottom: 'var(--space-md)' }}>Overview</h3>
              <p className="text-secondary">
                NuMuN Next ("we", "our", or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, and safeguard your information 
                when you use our learning platform.
              </p>
            </section>

            <section>
              <h3 style={{ marginBottom: 'var(--space-md)' }}>Information We Collect</h3>
              <p className="text-secondary" style={{ marginBottom: 'var(--space-sm)' }}>
                We collect information that you provide directly to us, including:
              </p>
              <ul style={{ 
                color: 'var(--text-secondary)',
                paddingLeft: 'var(--space-xl)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-xs)'
              }}>
                <li>Account information (email, name)</li>
                <li>Learning progress and task completion data</li>
                <li>Wellness data from connected services (with your explicit consent)</li>
              </ul>
            </section>

            <section>
              <h3 style={{ marginBottom: 'var(--space-md)' }}>Third-Party Integrations</h3>
              <p className="text-secondary">
                When you connect third-party services like Whoop, we access only the data 
                you explicitly authorize. This data is used solely to provide personalized 
                recommendations based on your recovery and energy levels. We never sell or 
                share this data with third parties.
              </p>
            </section>

            <section>
              <h3 style={{ marginBottom: 'var(--space-md)' }}>Data from Whoop</h3>
              <p className="text-secondary" style={{ marginBottom: 'var(--space-sm)' }}>
                If you choose to connect your Whoop account, we may access:
              </p>
              <ul style={{ 
                color: 'var(--text-secondary)',
                paddingLeft: 'var(--space-xl)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-xs)'
              }}>
                <li>Recovery scores</li>
                <li>Sleep performance metrics</li>
                <li>Strain and workout data</li>
                <li>Heart rate variability (HRV)</li>
                <li>Basic profile information</li>
              </ul>
              <p className="text-secondary" style={{ marginTop: 'var(--space-md)' }}>
                This data is used exclusively to tailor learning recommendations to your 
                current physical state. You can disconnect your Whoop account at any time 
                through Settings.
              </p>
            </section>

            <section>
              <h3 style={{ marginBottom: 'var(--space-md)' }}>How We Use Your Information</h3>
              <ul style={{ 
                color: 'var(--text-secondary)',
                paddingLeft: 'var(--space-xl)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-xs)'
              }}>
                <li>Provide and improve our learning platform</li>
                <li>Generate personalized task recommendations</li>
                <li>Track your learning progress</li>
                <li>Send important account notifications</li>
              </ul>
            </section>

            <section>
              <h3 style={{ marginBottom: 'var(--space-md)' }}>Data Security</h3>
              <p className="text-secondary">
                We implement appropriate technical and organizational measures to protect 
                your personal information. All data is encrypted in transit and at rest. 
                Access to your data is strictly limited to authorized personnel.
              </p>
            </section>

            <section>
              <h3 style={{ marginBottom: 'var(--space-md)' }}>Your Rights</h3>
              <p className="text-secondary">You have the right to:</p>
              <ul style={{ 
                color: 'var(--text-secondary)',
                paddingLeft: 'var(--space-xl)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-xs)'
              }}>
                <li>Access your personal data</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Disconnect any third-party integrations</li>
                <li>Export your data</li>
              </ul>
            </section>

            <section>
              <h3 style={{ marginBottom: 'var(--space-md)' }}>Contact Us</h3>
              <p className="text-secondary">
                If you have any questions about this Privacy Policy, please contact us at{' '}
                <a href="mailto:privacy@numuntechnologies.com" style={{ color: 'var(--accent-blue)' }}>
                  privacy@numuntechnologies.com
                </a>
              </p>
            </section>
          </div>
        </div>

        <p className="text-tertiary" style={{ 
          textAlign: 'center',
          marginTop: 'var(--space-2xl)'
        }}>
          © 2025 NuMuN. Learn. Grow. Prove. Advance.
        </p>
      </motion.div>
    </div>
  )
}
